import { Component, EventEmitter, Input, ViewChild } from '@angular/core';
import { CreateProductPlanComponent } from 'components/accounting/product-plan/create-product-plan.component';
import { ProductPlanComponent } from 'components/accounting/product-plan/product-plan.component';
import { TransferWizardComponent } from 'components/accounting/transfer-wizard/transfer-wizard.component';
import { ViewCompanyComponent } from 'components/company/viewcompany.component';
import { CreateSettlementComponent } from 'components/settlement/create-settlement.component';
import { CredentialType } from 'enums/CredentialType';
import { AccountingPlanModel } from 'models/generated/Entities.Accounting.AccountingPlan';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { TranslatePipe } from 'pipes/translate.pipe';
import { AccountingService } from 'services/accounting.service';
import { CompanyDataProvider, CompanyDataProviderFactory } from 'services/company-data-provider';
import { ComponentUrl, NavigationService, UrlParam } from 'services/navigation.service';
import { NotificationService } from 'services/notification.service';
import { CompanyActiveSettlement } from 'services/legal-case.service';
import { CompanyProductsLedgerPostsComponent } from 'components/company/company-products-ledger-posts.component';
import { CompanyProductsListComponent } from 'components/company/company-products-list.component';
import { TerminateProductPlansComponent } from 'components/accounting/product-plan/terminate-product-plans.component';
import { ProductPlanTagConstants } from 'constants/ProductPlanTagConstants';
import { SyncronizePlansToStandoutCrmComponent } from 'components/company/syncronize-plans-to-standout-crm.component';
import { LegalCaseModel } from 'models/generated/Entities.GormCrm.LegalCase';

@Component({
	selector: 'company-products',
	templateUrl: './company-products.component.html',
	providers: [CompanyDataProviderFactory],
	imports: [Layout, ListView, CompanyProductsListComponent, ProductPlanComponent, CompanyProductsLedgerPostsComponent, TranslatePipe]
})
@ComponentUrl('company-products')
export class CompanyProductsComponent {

	@ViewChild('productPlanList') productPlanList?: CompanyProductsListComponent;

	private _companyId = '';
	private _selectedProductPlan: ProductPlanModel | undefined;
	allProductPlans: ProductPlanModel[] = [];
	plansAvailableForStandoutSyncronization: ProductPlanModel[] = [];
	loading: boolean = true;
	noHandlingFees?: boolean;
	dataProvider?: CompanyDataProvider;
	settlementInfo?: CompanyActiveSettlement;
	companyLegalCases?: LegalCaseModel[];

	planTagsForStandoutSyncronization: string[] = [ProductPlanTagConstants.ads, ProductPlanTagConstants.seo, ProductPlanTagConstants.socialMedia];

	credentialTypeEnum = CredentialType;

	productPlanIdChange = new EventEmitter<string>();
	
	@UrlParam
	@Input()
	set companyId(id: string) {
		this._companyId = id;
		if (id) {
			this.navigation.openNavigationView(this, 'company-related-views', ViewCompanyComponent)
				.then(viewCompany => { viewCompany.companyId = id });

			this.dataProvider = this.dataProviderFactory.getProviderForCompanyId(id);

			this.loadProductPlans(false);
			this.dataProvider!.getCompanyAccountingSettings().then(settings => {
				// TODO (#23978): Per-brand NoHandlingFees
				this.noHandlingFees = settings[0]?.noHandlingFees ? true : false;
			});
		}
	}
	get companyId() {
		return this._companyId;
	}

	@UrlParam
	@Input() selectedProductPlanId?: string;

	set selectedProductPlan(plan: ProductPlanModel | undefined) {
		if (!plan || this._selectedProductPlan === plan) {
			this.selectedAccountingPlan = undefined;
			this._selectedProductPlan = undefined;
		}
		else {
			if (!plan.accountingPlans) {
				this.dataProvider!.getAllProductPlansAndAccountingPlans().then(plans => {
					plan.accountingPlans = plans?.find(pp => pp.id == plan.id)?.accountingPlans;
					this.selectedAccountingPlan = this.accountingService.filterCurrentAccountingPlan(plan.accountingPlans);
				});
			}
			else { 
				this.selectedAccountingPlan = this.accountingService.filterCurrentAccountingPlan(plan.accountingPlans);
			}
			this._selectedProductPlan = plan;
		}
		this.selectedProductPlanId = this._selectedProductPlan?.id;
		this.productPlanIdChange.emit(this.selectedProductPlanId);
		this.navigation.refreshUrl();
	}
	get selectedProductPlan() {
		return this._selectedProductPlan;
	}

	selectedAccountingPlan?: AccountingPlanModel;

	loadProductPlans(ignoreCache: boolean) {
		this.loading = true;

		let promises = [];
		promises.push(this.dataProvider!.getSettlement().then(s => { this.settlementInfo = s; }));
		promises.push(this.dataProvider!.getLegalCases().then(lc => { this.companyLegalCases = lc; }));
		promises.push(this.dataProvider!.getPlanReplacements());
		promises.push(
			this.dataProvider!.getAllProductPlansAndAccountingPlans(ignoreCache).then(async plans => {

				this.allProductPlans = plans;

				if (this.selectedProductPlanId !== undefined) {
					this.selectedProductPlan = plans.find(pp => pp.id == this.selectedProductPlanId);
				}
				this.plansAvailableForStandoutSyncronization = plans.filter(pp => pp.productPlanTags && pp.productPlanTags.some(t => this.planTagsForStandoutSyncronization.includes(t.tag)) && !pp.productPlanTags.map(t => t.tag).includes(ProductPlanTagConstants.standoutSyncActive));
			})
		);

		Promise.all(promises).then(() => { this.loading = false; });
	}

	createProductPlan() {
		this.navigation.openPopup(CreateProductPlanComponent)
			.then(view => {
				view.companyId = this.companyId;
			});
	}

	async toggleHandlingFees() {
		const newSetting = !this.noHandlingFees;
		await this.accountingService.updateNoHandlingFees(this.companyId, newSetting);
		this.noHandlingFees = newSetting;
	}

	createSettlement() {
		if (this.settlementInfo?.activeSettlement) {
			this.notificationService.warning('company.create-settlement.warning-already-active-settlement');
			return;
		}
		this.navigation.openPopup(CreateSettlementComponent, 'company.create-settlement.popup-header').then(view => {
			view.companyId = this.companyId;
		});
	}

	createTransfer() {
		this.navigation.openPopup(TransferWizardComponent, 'company.create-transfer.popup-header').then(view => {
			view.companyId = this.companyId;
		});
	}

	get terminatableProductPlans() {
		return this.allProductPlans.filter(pp => !!pp.deliveryDate && !pp.terminationDate && pp.accountingPlans && pp.accountingPlans.some((ap: AccountingPlanModel) => ap.endDate && ap.endDate >= new Date()));
	}

	terminateAllproductPlans() {
		this.navigation.openPopup(TerminateProductPlansComponent, 'company.product-plans.terminate-all.popup-header').then(view => {
			view.companyId = this.companyId;
			view.plansToTerminate = this.terminatableProductPlans;
		});
	}

	selectProductPlan(productPlanId: string) {
		if (productPlanId != undefined && productPlanId != this.selectedProductPlanId) {
			this.selectedProductPlan = this.allProductPlans.find(pp => pp.id == productPlanId);
		}
	}

	selectAccountingPlan(accountingPlan: AccountingPlanModel) {
		if (accountingPlan?.productPlanId != undefined && accountingPlan!.id != this.selectedAccountingPlan?.id) {
			this.selectedAccountingPlan = accountingPlan;
		}
	}

	syncPlansToStandoutCrm() {
		this.navigation.openPopup(SyncronizePlansToStandoutCrmComponent, 'company.syncronize-plans-to-standout-crm.popup-header').then(view => {
			view.planTagsForStandoutSyncronization = this.planTagsForStandoutSyncronization;
			view.companyId = this.companyId;
		});
	}

	constructor(
		private readonly dataProviderFactory: CompanyDataProviderFactory,
		private readonly accountingService: AccountingService,
		private readonly navigation: NavigationService,
		private readonly notificationService: NotificationService) {
	}
}
